import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'antd';

import { PixActions, AccountActions } from '@portal/actions';
import { moneyFormat } from '@portal/utils/format';
import { translate } from '@portal/services/i18n';
import { pix } from '@portal/config/app.json';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import useReduxState from '@portal/hooks/useReduxState';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';

type IProps = {
  visible: boolean;
  onCloseModal: () => void;
};

const CloseAccountModal: React.FC<IProps> = ({
  visible,
  onCloseModal,
}: IProps) => {
  const {
    pix: { pixKeys },
    account: { balance },
    user: { me: loggedUser },
  } = useReduxState();

  const dispatch = useDispatch();
  const [comment, setComment] = useState('');

  const handleCloseModal = () => {
    onCloseModal();
    setComment('');
  };

  const handleConfirm = () => {
    dispatch(AccountActions.requestClosure(comment));
    handleCloseModal();
  };

  const isDisabled = (): boolean => {
    if (!paymentAccount) return false;

    if (balance === null) return true;
    if (Number(balance.available) > 0) return true;
    if (pix.enabled && Array.isArray(pixKeys) && pixKeys.length > 0)
      return true;
    return false;
  };

  const paymentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ACCOUNT
  );

  useEffect(() => {
    if (pix.enabled && paymentAccount) {
      dispatch(PixActions.getAllPixKeys());
    }
    if (paymentAccount) {
      dispatch(AccountActions.getUserBalance());
    }
  }, [dispatch, paymentAccount]);

  return (
    <Modal
      title={translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.TITLE')}
      open={visible}
      onCancel={() => handleCloseModal()}
      footer={[
        <div className="close-account-modal__footer" key={'confirm'}>
          <AdvancedButton
            key="upload"
            text={translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.BUTTON.CONFIRM')}
            onClick={() => handleConfirm()}
            disabled={isDisabled()}
          />
        </div>,
      ]}
    >
      <div className="close-account-modal">
        <p>
          <h4>{translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.TEXT')}</h4>
          <br />
          <b>
            {`${translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.BALANCE')}: `}
          </b>
          {balance ? moneyFormat(Number(balance.available)) : moneyFormat(0)}
          <br />
          {pix.enabled ? (
            <>
              <b>
                {`${translate(
                  'COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.PIX_KEYS'
                )}: `}
              </b>
              {pixKeys ? pixKeys.length : 0}
            </>
          ) : null}
        </p>
        <AdvancedInput
          multiline
          value={comment}
          onChange={(newComment: string) => setComment(newComment)}
          placeholder={translate(
            'COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.PLACEHOLDER'
          )}
        />
      </div>
    </Modal>
  );
};

export default CloseAccountModal;
